import React, { useEffect, useState } from "react";
import { VerticalTimeline, VerticalTimelineElement } from 'react-vertical-timeline-component';
import { motion } from "framer-motion";
import 'react-vertical-timeline-component/style.min.css';

const workExperience = [
  {
    id: 1,
    title: "Research Technician I",
    company: "Weill Cornell Medicine",
    location: "New York, NY",
    period: "Feb 2023 – Present",
    type: "Research",
    points: [
      "Manually counted cells and conducted imaging experiments to model double-stranded DNA breaks, contributing to a manuscript in preparation for Cancer Discovery",
      "Presented poster, 'Ultraound as a novel method of assessing tumor burden in mice,' at Weill Cornell Medicine, Department of Medicine (DOM), Ninth Annual Research Retreat",
      "Developed a cross-platform desktop grant management system, optimizing efficiency to track grant budgets, expenditures, and compliance rules, using Python and Tkinter",
      "Constructed a Slackbot to handle purchase requests via slash commands, enabling real-time submission and tracking directly in Slack, using Python, Flask, and ngrok"
    ]
  },
  {
    id: 2,
    title: "Intern",
    company: "Aquavit Pharmaceuticals",
    location: "Remote",
    period: "June 2021 – September 2021",
    type: "Research",
    points: [
      "Researched 250+ biotech/CRO companies, updated SOPs, and drafted validation reports for product development"
    ]
  },
  {
    id: 3,
    title: "Teaching Assistant",
    company: "Temple University College of Engineering",
    location: "Philadelphia, PA",
    period: "September 2019 – May 2020",
    type: "Other",
    points: [
      "Supported MATLAB coursework, held office hours, and managed attendance for 250+ engineering students"
    ]
  },
  {
    id: 4,
    title: "Associate Director of Development",
    company: "KCUL Foundation",
    location: "Philadelphia, PA",
    period: "Sep 2019 – Present",
    type: "Other",
    points: [
      "Lead fundraising and PR initiatives for the annual Kimchi Festival at Love Park, drawing thousands of attendees and major national and international media coverage such as WHYY, Fox News, and YTN",
      "Contributed to securing $50k+ in nonprofit grants and supplemental funding through sponsorships and event-driven fundraising",
      "Partner with the CEO to design and implement new cultural events and educational workshops, expanding community engagement and donor reach"
    ]
  },
  {
    id: 5,
    title: "Volunteer Basketball Coach",
    company: "Apex for Youth",
    location: "Brooklyn, NY",
    period: "Sep 2024 – Present",
    type: "Other",
    points: [
      "Mentor underprivileged Asian immigrant youth (ages 8-13) in basketball fundamentals, teamwork, and confidence building",
      "Facilitate skill development sessions for large groups of 30+ students, fostering an inclusive, structured, and supportive learning environment"
    ]
  },
  {
    id: 6,
    title: "Full‑Stack Developer",
    company: "Wave Dating App",
    location: "Remote",
    period: "Mar 2020 – Present",
    type: "Development",
    points: [
      "React Native video‑first app (1.3K+ downloads, 4.9/5 rating)",
      "Marketing site with custom Node.js build and animations"
    ]
  }
];

const colorByType = {
  Research: '#3b82f6',
  Development: '#10b981',
  Other: '#f59e0b'
};

const iconByType = {
  Research: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
      <path d="M10 2a1 1 0 0 0-1 1v6.586l-5.707 5.707a1 1 0 0 0 1.414 1.414L10 11.414V21a1 1 0 1 0 2 0v-9.586l5.293 5.293a1 1 0 0 0 1.414-1.414L13 9.586V3a1 1 0 0 0-1-1h-2z"/>
    </svg>
  ),
  Development: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
      <path d="M8.293 6.293 2.586 12l5.707 5.707 1.414-1.414L5.414 12l4.293-4.293-1.414-1.414zM15.707 6.293l-1.414 1.414L18.586 12l-4.293 4.293 1.414 1.414L21.414 12l-5.707-5.707z"/>
    </svg>
  ),
  Other: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
      <circle cx="12" cy="12" r="6"/>
    </svg>
  )
};

// Motion variants
const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } }
};

const listStagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } }
};

const listItem = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25, ease: "easeOut" } }
};

export default function Work() {
  const [filter, setFilter] = useState('All');

  useEffect(() => {
    const prevent = e => e.preventDefault();
    document.addEventListener("contextmenu", prevent);
    return () => document.removeEventListener("contextmenu", prevent);
  }, []);

  const filtered = filter === 'All' ? workExperience : workExperience.filter(w => w.type === filter);

  return (
    <div className="min-h-screen transition-colors" style={{ backgroundColor: 'inherit' }}>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header + Filters */}
        <motion.div
          className="mb-6 text-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
        >
          <h1 className="text-4xl font-bold mb-3" style={{ color: '#f6e05e' }}>Work</h1>
          <p className="text-muted-foreground mb-4">Timeline of experience and research</p>

          <div className="flex flex-wrap gap-2 justify-center">
            {['All', 'Research', 'Development'].map(cat => (
              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.98 }}
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                  filter === cat ? 'bg-primary text-primary-foreground' : 'bg-card text-card-foreground hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                {cat}
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Timeline */}
        <VerticalTimeline layout="1-column-left" animate={true} lineColor={'hsl(var(--border))'}>
          {filtered.map((item) => (
            <motion.div
              key={item.id}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeUp}
            >
              <VerticalTimelineElement
                contentClassName="modern-timeline-card"
                contentStyle={{
                  background: 'hsla(var(--card), 0.85)',
                  color: 'hsl(var(--card-foreground))',
                  border: '1px solid hsl(var(--border))',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
                  backdropFilter: 'blur(8px)'
                }}
                contentArrowStyle={{ borderRight: '7px solid hsla(var(--card), 0.85)' }}
                date={item.period}
                dateClassName="modern-timeline-date"
                iconStyle={{
                  background: colorByType[item.type] || '#6b7280',
                  color: '#fff',
                  boxShadow: '0 0 0 6px rgba(0,0,0,0.08)'
                }}
                icon={<div style={{ display: 'grid', placeItems: 'center', height: '100%', color: '#fff' }}>{iconByType[item.type] || iconByType.Other}</div>}
              >
                <h3 className="text-xl font-semibold text-card-foreground">{item.title}</h3>
                <h4 className="text-primary font-medium">{item.company} • {item.location}</h4>

                <motion.ul className="mt-3 space-y-1" variants={listStagger} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.4 }}>
                  {item.points.map((p, idx) => (
                    <motion.li key={idx} className="text-sm text-muted-foreground" variants={listItem}>• {p}</motion.li>
                  ))}
                </motion.ul>
              </VerticalTimelineElement>
            </motion.div>
          ))}
        </VerticalTimeline>
      </div>
    </div>
  );
} 